import { Timestamp, DocumentReference } from 'firebase/firestore';

export interface ApiToken {
	id: string;
	name: string; // User-friendly name for the token
	token: string; // The actual token string (used as document ID in tokens collection)
	userId: string; // User ID who owns this token
	userRef: DocumentReference | null; // Reference to user document
	createdAt: Timestamp | null;
	expiresAt: Timestamp | null; // null means never expires
	lastUsed: Timestamp | null;
	active: boolean; // false = revoked, user cannot generate new token
	requestCount: number; // Total requests made today
	lastRequestDate: string; // Date string (YYYY-MM-DD) of last request
}

/**
 * Generate a secure random API token
 * Format: cybercore_[random-string]
 */
export const generateApiToken = (): string => {
	const prefix = 'cybercore_';
	const length = 32;
	const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

	// Use crypto.getRandomValues for secure random generation
	const randomValues = new Uint8Array(length);
	crypto.getRandomValues(randomValues);

	let token = '';
	for (let i = 0; i < length; i++) {
		token += chars[randomValues[i] % chars.length];
	}

	return prefix + token;
};

/**
 * Generate a unique token ID
 */
export const generateTokenId = (): string => {
	return `token_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Create a new API token object
 */
export const createApiToken = (name: string, userId: string, userRef: DocumentReference | null, expiresInDays?: number): ApiToken => {
	const now = Timestamp.now();
	let expiresAt: Timestamp | null = null;

	if (expiresInDays && expiresInDays > 0) {
		const expireDate = new Date();
		expireDate.setDate(expireDate.getDate() + expiresInDays);
		expiresAt = Timestamp.fromDate(expireDate);
	}

	return {
		id: generateTokenId(),
		name: name || 'API Token',
		token: generateApiToken(),
		userId,
		userRef,
		createdAt: now,
		expiresAt,
		lastUsed: null,
		active: true,
		requestCount: 0,
		lastRequestDate: '',
	};
};

/**
 * Check if a token is expired
 */
export const isTokenExpired = (token: ApiToken): boolean => {
	if (!token.expiresAt) {
		return false; // Never expires
	}

	const now = new Date();
	const expiryDate = token.expiresAt.toDate();
	return now > expiryDate;
};

/**
 * Check if a token is valid (active and not expired)
 */
export const isTokenValid = (token: ApiToken): boolean => {
	return token.active && !isTokenExpired(token);
};

/**
 * Get default API token object
 */
export const getDefaultApiToken = (): ApiToken => {
	return {
		id: '',
		name: '',
		token: '',
		userId: '',
		userRef: null,
		createdAt: null,
		expiresAt: null,
		lastUsed: null,
		active: false,
		requestCount: 0,
		lastRequestDate: '',
	};
};

/**
 * Format expiry date for display
 */
export const formatExpiryDate = (token: ApiToken): string => {
	if (!token.expiresAt) {
		return 'Never';
	}

	const expiryDate = token.expiresAt.toDate();
	return expiryDate.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	});
};

/**
 * Format last used date for display
 */
export const formatLastUsed = (token: ApiToken): string => {
	if (!token.lastUsed) {
		return 'Never';
	}

	const lastUsedDate = token.lastUsed.toDate();
	const now = new Date();
	const diffMs = now.getTime() - lastUsedDate.getTime();
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

	if (diffDays === 0) {
		return 'Today';
	} else if (diffDays === 1) {
		return 'Yesterday';
	} else if (diffDays < 7) {
		return `${diffDays} days ago`;
	} else {
		return lastUsedDate.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		});
	}
};

/**
 * Mask token for display (show only first and last 4 characters)
 */
export const maskToken = (token: string): string => {
	if (token.length <= 12) {
		return token; // Too short to mask meaningfully
	}

	const prefix = token.substring(0, 15); // "cybercore_" + 5 chars
	const suffix = token.slice(-4);
	const masked = '•'.repeat(Math.max(0, token.length - 19));

	return `${prefix}${masked}${suffix}`;
};

/**
 * Calculate daily request limit based on token age
 * Day 1: 5 requests, Day 2: 10 requests, ..., Max: 50 requests/day
 */
export const getDailyRequestLimit = (token: ApiToken): number => {
	if (!token.createdAt) {
		return 5; // Default to minimum if no creation date
	}

	const createdDate = token.createdAt.toDate();
	const now = new Date();

	// Calculate days since creation (including partial days as full days)
	const msPerDay = 24 * 60 * 60 * 1000;
	const daysSinceCreation = Math.floor((now.getTime() - createdDate.getTime()) / msPerDay) + 1;

	// 5 requests per day of age, capped at 50
	const limit = Math.min(daysSinceCreation * 5, 50);

	return limit;
};

/**
 * Get today's date string in YYYY-MM-DD format
 */
export const getTodayDateString = (): string => {
	const now = new Date();
	return now.toISOString().split('T')[0];
};

/**
 * Check if token has exceeded daily rate limit
 */
export const hasExceededRateLimit = (token: ApiToken): boolean => {
	const today = getTodayDateString();
	const limit = getDailyRequestLimit(token);

	// If it's a new day, the request count should be reset (done server-side)
	if (token.lastRequestDate !== today) {
		return false;
	}

	return token.requestCount >= limit;
};

/**
 * Get remaining requests for today
 */
export const getRemainingRequests = (token: ApiToken): number => {
	const today = getTodayDateString();
	const limit = getDailyRequestLimit(token);

	// If it's a new day, all requests are remaining
	if (token.lastRequestDate !== today) {
		return limit;
	}

	return Math.max(0, limit - token.requestCount);
};
